import { api } from '../api';
import { Banner, Pill, Spinner, useAsync } from '../components/Common';
import { formatDateTime } from '../format';

interface RunRow {
  id: string;
  report_id: number;
  report_name: string;
  status: string;
  started_at: string;
  duration_ms: number | null;
  trigger: string;
  run_by?: string;
}

export function History({ onOpenRun }: { onOpenRun: (id: string) => void }) {
  const { data, error, loading } = useAsync(() => api.get<RunRow[]>('/api/runs?limit=100'));

  if (loading && !data) return <Spinner label="Loading run history…" />;
  if (error) return <Banner tone="error">{error}</Banner>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Run history</h1>
          <p>
            Every run is kept with its figures, so an old report can be reopened exactly as it was generated — useful when
            someone questions a number in a PDF from three weeks ago.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Report</th>
                <th>When</th>
                <th>Trigger</th>
                {data?.some((r) => r.run_by) && <th>Run by</th>}
                <th className="right">Duration</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((run) => (
                <tr key={run.id}>
                  <td>{run.report_name}</td>
                  <td>{formatDateTime(run.started_at)}</td>
                  <td>{run.trigger}</td>
                  {data?.some((r) => r.run_by) && <td>{run.run_by ?? '—'}</td>}
                  <td className="right">{run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)} s` : '—'}</td>
                  <td>
                    <Pill tone={run.status === 'ok' ? 'ok' : run.status === 'partial' ? 'warn' : 'bad'}>{run.status}</Pill>
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="small" onClick={() => onOpenRun(run.id)}>
                      Open
                    </button>{' '}
                    <a className="btn small" href={`/api/runs/${run.id}/export.xlsx`}>
                      Excel
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(data ?? []).length && <p className="note">No runs yet.</p>}
      </div>
    </>
  );
}
